package com.schoolconnect.app.feature.timetable

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.style.TextOverflow
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDatePickerDialog
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogMultiSelectField
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppSecondaryBackground
import com.schoolconnect.app.datasource.network.request.TimetableCellBulkCreateRequest
import com.schoolconnect.app.datasource.network.request.TimetableCombinedCreateRequest
import com.schoolconnect.app.datasource.network.request.TimetableSlotBulkCreateRequest
import com.schoolconnect.app.datasource.network.response.TimetableViewResponse
import com.schoolconnect.app.designsystem.list.AdminListHorizontalScrollbar
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.chevron_left
import ischoolconnect.sharedui.generated.resources.chevron_right
import ischoolconnect.sharedui.generated.resources.calendar
import ischoolconnect.sharedui.generated.resources.clock
import ischoolconnect.sharedui.generated.resources.plus
import ischoolconnect.sharedui.generated.resources.trash
import org.jetbrains.compose.resources.painterResource
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus

private val EditorHeaderBg = Color(0xFFF8FAFF)
private val EditorHolidayBg = Color(0xFFF1F3F6)
private fun TimeTableStatusColor(status: String) = if (status == "Published") Color(0xFF159447) else Color(0xFFF06423)
private val EditorDays = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat")
internal val EditorAllDays = EditorDays + "Sun"
private val EditorWeeklyDays = EditorAllDays
private val EditorDayOptions = listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday")

internal data class EditorSlot(val label: String)
internal data class SpecialDaySchedule(val day: String, val slots: List<EditorSlot>, val values: List<String>)
private data class SlotPickerTarget(val specialDay: String?, val slotIndex: Int = -1, val initialRange: String = "")

@Composable
internal fun EnterTimeTableDialog(schoolLabel: String, classSectionId: String? = null, timetableHeaderId: String? = null, initialTimetable: TimetableViewResponse? = null, timetableLoading: Boolean = false, timetableError: String = "", classSections: List<TimetableClassSectionOption> = emptyList(), subjects: List<String> = emptyList(), subjectsLoading: Boolean = false, subjectsError: String = "", academicYearStartDate: String = "", academicYearEndDate: String = "", academicYearLoading: Boolean = false, academicYearError: String = "", onClassSectionSelected: (String) -> Unit = {}, onSave: ((String?, TimetableCombinedCreateRequest, () -> Unit) -> Unit)? = null, onDismiss: () -> Unit) {
    val isNewTimetable = timetableHeaderId.isNullOrBlank() && initialTimetable == null
    val initialOption = classSections.firstOrNull { it.id == classSectionId } ?: classSections.firstOrNull()
    val orderedClassSections = remember(classSections) {
        classSections.sortedWith { left, right ->
            compareTimetableClassSections(
                left.className,
                left.sectionName,
                right.className,
                right.sectionName
            )
        }
    }
    var selectedClass by remember(classSections, classSectionId) { mutableStateOf(initialOption?.className.orEmpty()) }
    var selectedSection by remember(classSections, classSectionId) { mutableStateOf(initialOption?.sectionName.orEmpty()) }
    var selectedClassSectionId by remember(classSections, classSectionId) { mutableStateOf(classSectionId ?: initialOption?.id.orEmpty()) }
    LaunchedEffect(selectedClassSectionId) {
        onClassSectionSelected(selectedClassSectionId)
    }
    var publishedTill by remember(selectedClassSectionId, timetableHeaderId, initialTimetable) { mutableStateOf(initialTimetable?.publishedTill?.let(::formatPreviewDate).orEmpty()) }
    var weekStartDate by remember(selectedClassSectionId, timetableHeaderId, initialTimetable, academicYearStartDate) {
        mutableStateOf(
            academicYearStartDate.takeIf { it.isNotBlank() }?.let { value ->
                normalizedWeekStart(value)?.toString().orEmpty()
            } ?: initialTimetable?.fromDate?.let { value ->
                normalizedWeekStart(value)?.let { formatPreviewDate(it.toString()) } ?: formatPreviewDate(value)
            }.orEmpty()
        )
    }
    var weekEndStartDate by remember(selectedClassSectionId, timetableHeaderId, initialTimetable, academicYearEndDate) {
        mutableStateOf(
            academicYearEndDate.takeIf { it.isNotBlank() }?.let { value ->
                normalizedWeekStart(value)?.toString().orEmpty()
            } ?: initialTimetable?.toDate?.let { value ->
                normalizedWeekStart(value)?.let { formatPreviewDate(it.toString()) }
            }.orEmpty()
        )
    }
    var specialDays by remember(selectedClassSectionId, timetableHeaderId, initialTimetable) { mutableStateOf(initialTimetable?.toSpecialDays().orEmpty()) }
    var weeklyOffDays by remember(selectedClassSectionId, timetableHeaderId, initialTimetable) { mutableStateOf(initialTimetable?.toWeeklyOffDays().orEmpty()) }
    var slots by remember(selectedClassSectionId, timetableHeaderId, initialTimetable) { mutableStateOf(initialTimetable?.toPreviewSlots().orEmpty()) }
    var values by remember(selectedClassSectionId, timetableHeaderId, initialTimetable) { mutableStateOf(initialTimetable?.toPreviewValues().orEmpty()) }
    var specialSchedules by remember(selectedClassSectionId, timetableHeaderId, initialTimetable) { mutableStateOf(initialTimetable?.toSpecialSchedules().orEmpty()) }
    var preview by remember { mutableStateOf(false) }
    var showPreview by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    var slotPickerTarget by remember { mutableStateOf<SlotPickerTarget?>(null) }
    var weekRangePickerOpen by remember { mutableStateOf(false) }
    val selectedWeekRangeLabel = timetableWeekRangeLabel(weekStartDate, weekEndStartDate)

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, dialogWidthOverride = 1480.dp, maxDialogHeight = 900.dp) {
        AppDialogHeader(
            title = if (preview) "Preview Time Table" else "Enter Time Table",
            subtitle = if (preview) "Review the current weekly timetable before saving." else "Enter or update the weekly timetable for the selected class.",
            onClose = onDismiss,
            colors = AppDialogDefaultColors,
            height = 92.dp
        )
        AppDialogErrorMessage(
            message = listOf(timetableError, subjectsError, academicYearError)
                .filter { it.isNotBlank() }
                .distinct()
                .joinToString("\n"),
            modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp)
        )
        AppDialogDivider()
        AppDialogBody(horizontalPadding = 28.dp, verticalPadding = 20.dp) { compact ->
            Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
                if (!preview) {
                    if (compact) {
                        EditorSelect(
                            "Class / Section",
                            orderedClassSections.firstOrNull { it.id == selectedClassSectionId }?.displayLabel().orEmpty(),
                            orderedClassSections.map { it.displayLabel() },
                            Modifier.fillMaxWidth(),
                            fieldHeight = 44.dp
                        ) { label ->
                            orderedClassSections.firstOrNull { it.displayLabel() == label }?.let { option ->
                                selectedClass = option.className
                                selectedSection = option.sectionName
                                selectedClassSectionId = option.id
                            }
                        }
                        EditorMultiSelect("Special Schedule Day", specialDays, EditorDayOptions.filter { it !in weeklyOffDays }, Modifier.fillMaxWidth(), fieldHeight = 44.dp) {
                            specialDays = it
                            val sharedSlots = specialSchedules.firstOrNull()?.slots.orEmpty()
                            specialSchedules = it.map { day ->
                                specialSchedules.firstOrNull { schedule -> schedule.day == day }
                                    ?: SpecialDaySchedule(day, sharedSlots, List(sharedSlots.size) { "" })
                            }
                        }
                        EditorMultiSelect("Weekly Off Day", weeklyOffDays, EditorDayOptions.filter { it !in specialDays }, Modifier.fillMaxWidth(), fieldHeight = 44.dp) { weeklyOffDays = it }
                    } else {
                        Row(horizontalArrangement = Arrangement.spacedBy(18.dp), verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth()) {
                            EditorSelect(
                                "Class / Section",
                                orderedClassSections.firstOrNull { it.id == selectedClassSectionId }?.displayLabel().orEmpty(),
                                orderedClassSections.map { it.displayLabel() },
                                Modifier.weight(1f),
                                fieldHeight = 44.dp
                            ) { label ->
                                orderedClassSections.firstOrNull { it.displayLabel() == label }?.let { option ->
                                    selectedClass = option.className
                                    selectedSection = option.sectionName
                                    selectedClassSectionId = option.id
                                }
                            }
                            EditorMultiSelect("Special Schedule Day", specialDays, EditorDayOptions.filter { it !in weeklyOffDays }, Modifier.weight(1.4f), fieldHeight = 44.dp) {
                                specialDays = it
                                val sharedSlots = specialSchedules.firstOrNull()?.slots.orEmpty()
                                specialSchedules = it.map { day ->
                                    specialSchedules.firstOrNull { schedule -> schedule.day == day }
                                        ?: SpecialDaySchedule(day, sharedSlots, List(sharedSlots.size) { "" })
                                }
                            }
                            EditorMultiSelect("Weekly Off Day", weeklyOffDays, EditorDayOptions.filter { it !in specialDays }, Modifier.weight(1.4f), fieldHeight = 44.dp) { weeklyOffDays = it }
                        }
                    }
                }
                EditorGrid(
                    title = "Weekly Schedule",
                    slots = slots,
                    values = values,
                    days = EditorWeeklyDays.filter { day -> specialDays.none { it.take(3).equals(day, true) } },
                    allDays = EditorAllDays,
                    weeklyOffDays = weeklyOffDays,
                    readOnly = preview,
                    subjects = subjects,
                    onAddSlot = {
                        val nextRange = nextSlotRange(slots)
                        if (nextRange == null) message = "No time remains after the previous slot ends."
                        else slotPickerTarget = SlotPickerTarget(null, initialRange = nextRange)
                    },
                    onEditSlot = { index, slot -> slotPickerTarget = SlotPickerTarget(null, index, slot.label) },
                    onSlotChanged = { index, value ->
                    slots = slots.toMutableList().also { it[index] = value }
                    },
                    onSlotRemoved = { index ->
                        val previousSlotCount = slots.size
                        slots = slots.toMutableList().also { it.removeAt(index) }
                        values = values.chunked(previousSlotCount).flatMap { dayValues ->
                            dayValues.filterIndexed { slotIndex, _ -> slotIndex != index }
                        }
                    },
                    onValueChanged = { index, value ->
                    values = values.toMutableList().also { it[index] = value }
                    }
                )
                val sharedSpecialSlots = specialSchedules.firstOrNull()?.slots.orEmpty()
                val sharedSpecialValues = specialSchedules.flatMap { schedule ->
                    sharedSpecialSlots.indices.map { schedule.values.getOrElse(it) { "" } }
                }
                if (specialSchedules.isNotEmpty()) {
                    EditorGrid(
                        title = "${specialSchedules.joinToString(", ") { it.day.take(3) }} Schedule",
                        slots = sharedSpecialSlots,
                        values = sharedSpecialValues,
                        days = specialSchedules.map { it.day.take(3) },
                        allDays = specialSchedules.map { it.day.take(3) },
                        weeklyOffDays = emptyList(),
                        readOnly = preview,
                        subjects = subjects,
                        onAddSlot = {
                            val nextRange = nextSlotRange(sharedSpecialSlots)
                            if (nextRange == null) message = "No time remains after the previous slot ends."
                            else slotPickerTarget = SlotPickerTarget(specialSchedules.first().day, initialRange = nextRange)
                        },
                        onEditSlot = { index, slot -> slotPickerTarget = SlotPickerTarget(specialSchedules.first().day, index, slot.label) },
                        onSlotChanged = { index, slot ->
                            specialSchedules = replaceSharedSpecialSlot(specialSchedules, index, slot)
                        },
                        onSlotRemoved = { index ->
                            specialSchedules = removeSharedSpecialSlot(specialSchedules, index)
                        },
                        onValueChanged = { index, value ->
                            specialSchedules = updateSpecialScheduleCell(specialSchedules, sharedSpecialSlots.size, index, value)
                        }
                    )
                }
                if (timetableLoading) Text("Loading timetable for the selected class…", color = AppMuted, fontSize = 13.sp)
                if (subjectsLoading) Text("Loading subjects for the selected class…", color = AppMuted, fontSize = 13.sp)
                else if (selectedClassSectionId.isNotBlank() && subjects.isEmpty()) Text("No subjects are linked to the selected class.", color = AppMuted, fontSize = 13.sp)
                if (message.isNotBlank()) Text(message, color = AppPrimary, fontSize = 13.sp)
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = if (preview) listOf(AppDialogFooterAction("Close", onDismiss)) else listOf(
                AppDialogFooterAction("Cancel", onDismiss, width = 150.dp),
                AppDialogFooterAction("Save", {
                    val id = selectedClassSectionId
                    if (timetableLoading) message = "Wait for the selected class timetable to finish loading."
                    else if (isNewTimetable && academicYearLoading) message = "Wait for the current academic year to finish loading."
                    else if (isNewTimetable && academicYearError.isNotBlank()) message = academicYearError
                    else if (id.isNullOrBlank()) message = "Select a class timetable from the list before saving."
                    else if (weekStartDate.isBlank() || weekEndStartDate.isBlank()) message = "Select one or more timetable weeks before saving."
                    else onSave?.invoke(timetableHeaderId, buildTimetableRequest(id, selectedClass, selectedSection, weekStartDate, weekEndStartDate, slots, values, specialSchedules, specialDays, weeklyOffDays, "Not Published", initialTimetable), { onDismiss() })
                }, width = 170.dp),
                AppDialogFooterAction("Save & Publish", {
                    val id = selectedClassSectionId
                    if (timetableLoading) message = "Wait for the selected class timetable to finish loading."
                    else if (isNewTimetable && academicYearLoading) message = "Wait for the current academic year to finish loading."
                    else if (isNewTimetable && academicYearError.isNotBlank()) message = academicYearError
                    else if (id.isNullOrBlank()) message = "Select a class timetable from the list before saving."
                    else if (weekStartDate.isBlank() || weekEndStartDate.isBlank()) message = "Select one or more timetable weeks before saving."
                    else validateTimetableForPublish(slots, values, specialSchedules, specialDays, weeklyOffDays)?.let { message = it }
                        ?: onSave?.invoke(timetableHeaderId, buildTimetableRequest(id, selectedClass, selectedSection, weekStartDate, weekEndStartDate, slots, values, specialSchedules, specialDays, weeklyOffDays, "Published", initialTimetable), { onDismiss() })
                }, AppDialogActionStyle.Primary, width = 210.dp)
            ),
            horizontalArrangement = Arrangement.End,
            leadingContent = if (!preview) ({ EditorOutlineButton("Preview", Modifier.width(118.dp)) { showPreview = true } }) else null
        )
    }
    if (slotPickerTarget != null && !preview) {
        val target = slotPickerTarget!!
        TimeRangePickerDialog(
            initialRange = target.initialRange,
            onDismiss = { slotPickerTarget = null },
            onAdd = { range ->
                if (target.specialDay == null) {
                    if (target.slotIndex >= 0) slots = slots.toMutableList().also { it[target.slotIndex] = EditorSlot(range) }
                    else {
                        val previousSlotCount = slots.size
                        slots = slots + EditorSlot(range)
                        val nextSlotCount = slots.size
                        values = List(EditorAllDays.size * nextSlotCount) { index ->
                            val dayIndex = index / nextSlotCount
                            val slotIndex = index % nextSlotCount
                            if (slotIndex < previousSlotCount) values.getOrElse(dayIndex * previousSlotCount + slotIndex) { "" } else ""
                        }
                    }
                } else {
                    specialSchedules = if (target.slotIndex >= 0) {
                        replaceSharedSpecialSlot(specialSchedules, target.slotIndex, EditorSlot(range))
                    } else {
                        appendSharedSpecialSlot(specialSchedules, EditorSlot(range))
                    }
                }
                slotPickerTarget = null
            }
        )
    }
    if (showPreview) {
        PreviewTimeTableDialog(
            selectedClass = selectedClass,
            selectedSection = selectedSection,
            fromDate = weekStartDate,
            weekStartDate = weekStartDate,
            duration = selectedWeekRangeLabel,
            slots = slots,
            values = values,
            specialSchedules = specialSchedules,
            specialDays = specialDays,
            weeklyOffDays = weeklyOffDays,
            onDismiss = { showPreview = false }
        )
    }
    if (weekRangePickerOpen && !preview) {
        AppDialogDatePickerDialog(
            value = weekStartDate,
            onDismiss = { weekRangePickerOpen = false },
            onDateSelected = {},
            weekSelection = true,
            weekRangeSelection = true,
            weekRangeStartValue = weekStartDate,
            weekRangeEndValue = weekEndStartDate,
            onWeekRangeSelected = { start, end ->
                weekStartDate = normalizedWeekStart(start)?.toString().orEmpty()
                weekEndStartDate = normalizedWeekStart(end)?.toString().orEmpty()
            }
        )
    }
}

internal fun buildTimetableRequest(id: String, className: String, sectionName: String, weekStart: String, weekEndStart: String, slots: List<EditorSlot>, values: List<String>, specialSchedules: List<SpecialDaySchedule>, specialDays: List<String>, weeklyOffDays: List<String>, status: String, initialTimetable: TimetableViewResponse? = null): TimetableCombinedCreateRequest {
    val requestSlots = slots.mapIndexed { index, slot -> slot.toRequest("REGULAR", index + 1) } +
        specialSchedules.firstOrNull()?.slots.orEmpty().mapIndexed { index, slot -> slot.toRequest("SPECIAL", index + 1) }
    fun slotSignature(slot: TimetableSlotBulkCreateRequest) = "${slot.scheduleType}:${slot.slotOrder}:${slot.fromTime}:${slot.toTime}"
    val existingSlotSignatures = initialTimetable?.slots.orEmpty()
        .map { "${it.scheduleType.orEmpty().uppercase()}:${it.slotOrder}:${it.fromTime?.let(::apiTime).orEmpty()}:${it.toTime?.let(::apiTime).orEmpty()}" }
        .sorted()
    val requestedSlotSignatures = requestSlots.map(::slotSignature).sorted()
    val specialDayIndexes = specialDays.map(::editorDayIndex).distinct().sorted()
    val weeklyOffDayIndexes = weeklyOffDays.map(::editorDayIndex).distinct().filterNot { it in specialDayIndexes }.sorted()
    return TimetableCombinedCreateRequest(
    classSectionId = id,
    className = className,
    sectionName = sectionName,
    fromDate = apiDate(weekStart),
    toDate = (parseEditorDate(weekEndStart) ?: parseEditorDate(weekStart)!!).plus(6, DateTimeUnit.DAY).toString(),
    specialScheduleDays = specialDayIndexes,
    weeklyOffDays = weeklyOffDayIndexes,
    status = status,
    slots = requestSlots,
    replaceSlots = initialTimetable == null || existingSlotSignatures != requestedSlotSignatures,
    cells = (if (slots.isEmpty()) emptyList() else values.mapIndexedNotNull { index, value ->
        val dayIndex = index / slots.size
        val dayName = EditorAllDays.getOrNull(dayIndex)
        value.trim().takeIf {
            it.isNotBlank() &&
                dayName != null &&
                dayName !in weeklyOffDays.map { day -> day.take(3) } &&
                dayName !in specialDays.map { day -> day.take(3) }
        }
            ?.let { TimetableCellBulkCreateRequest("REGULAR", (index % slots.size) + 1, listOf(dayIndex), it) }
    }) + specialSchedules.flatMap { schedule ->
        val dayIndex = editorDayIndex(schedule.day)
        if (dayIndex in weeklyOffDays.map(::editorDayIndex)) return@flatMap emptyList()
        schedule.values.mapIndexedNotNull { index, value ->
            value.trim().takeIf { it.isNotBlank() && index < schedule.slots.size }
                ?.let { TimetableCellBulkCreateRequest("SPECIAL", index + 1, listOf(dayIndex), it) }
        }
    }
)
}

internal fun validateTimetableForPublish(
    slots: List<EditorSlot>,
    values: List<String>,
    specialSchedules: List<SpecialDaySchedule>,
    specialDays: List<String>,
    weeklyOffDays: List<String>
): String? {
    if (slots.isEmpty() && specialSchedules.none { it.slots.isNotEmpty() }) {
        return "Add at least one time slot before publishing."
    }
    if (slots.any { it.label.trim().isBlank() }) {
        return "Complete every regular time slot before publishing."
    }

    val specialDayPrefixes = specialDays.map { it.take(3) }.toSet()
    val weeklyOffPrefixes = weeklyOffDays.map { it.take(3) }.toSet()
    val activeRegularDays = EditorAllDays.filter { day ->
        day !in specialDayPrefixes && day !in weeklyOffPrefixes
    }
    activeRegularDays.forEach { day ->
        val dayIndex = EditorAllDays.indexOf(day)
        slots.indices.forEach { slotIndex ->
            val valueIndex = dayIndex * slots.size + slotIndex
            if (values.getOrNull(valueIndex).orEmpty().trim().isBlank()) {
                return "Complete the ${day} subject for period ${slotIndex + 1} before publishing."
            }
        }
    }

    specialDays.forEach { day ->
        val schedule = specialSchedules.firstOrNull { it.day.equals(day, ignoreCase = true) }
            ?: return "Add the ${day} special schedule before publishing."
        if (schedule.slots.isEmpty()) {
            return "Add at least one ${day} special time slot before publishing."
        }
        if (schedule.slots.any { it.label.trim().isBlank() }) {
            return "Complete every ${day} special time slot before publishing."
        }
        schedule.slots.indices.forEach { slotIndex ->
            if (schedule.values.getOrNull(slotIndex).orEmpty().trim().isBlank()) {
                return "Complete the ${day} subject for special period ${slotIndex + 1} before publishing."
            }
        }
    }
    return null
}

internal fun replaceSharedSpecialSlot(schedules: List<SpecialDaySchedule>, index: Int, slot: EditorSlot): List<SpecialDaySchedule> =
    schedules.map { schedule ->
        if (index !in schedule.slots.indices) schedule
        else schedule.copy(slots = schedule.slots.toMutableList().also { it[index] = slot })
    }

internal fun appendSharedSpecialSlot(schedules: List<SpecialDaySchedule>, slot: EditorSlot): List<SpecialDaySchedule> =
    schedules.map { schedule -> schedule.copy(slots = schedule.slots + slot, values = schedule.values + "") }

internal fun removeSharedSpecialSlot(schedules: List<SpecialDaySchedule>, index: Int): List<SpecialDaySchedule> =
    schedules.map { schedule ->
        if (index !in schedule.slots.indices) schedule
        else schedule.copy(
            slots = schedule.slots.filterIndexed { slotIndex, _ -> slotIndex != index },
            values = schedule.values.filterIndexed { slotIndex, _ -> slotIndex != index }
        )
    }

internal fun updateSpecialScheduleCell(
    schedules: List<SpecialDaySchedule>,
    slotCount: Int,
    flattenedIndex: Int,
    value: String
): List<SpecialDaySchedule> {
    if (slotCount <= 0) return schedules
    val scheduleIndex = flattenedIndex / slotCount
    val slotIndex = flattenedIndex % slotCount
    return schedules.mapIndexed { index, schedule ->
        if (index != scheduleIndex || slotIndex !in schedule.values.indices) schedule
        else schedule.copy(values = schedule.values.toMutableList().also { it[slotIndex] = value })
    }
}

private fun EditorSlot.toRequest(scheduleType: String, slotOrder: Int): TimetableSlotBulkCreateRequest {
    val parts = label.split(" – ", "-", limit = 2).map { it.trim() }
    return TimetableSlotBulkCreateRequest(
        scheduleType = scheduleType,
        slotOrder = slotOrder,
        fromTime = apiTime(parts.getOrNull(0).orEmpty()),
        toTime = apiTime(parts.getOrNull(1).orEmpty())
    )
}

private fun editorDayIndex(day: String): Int = EditorDayOptions.indexOfFirst { it.equals(day, true) }.coerceIn(0, 6)

private fun apiDate(value: String): String = parseEditorDate(value)?.toString() ?: value

private fun timetableWeekRangeLabel(startValue: String, endWeekStartValue: String): String {
    val start = parseEditorDate(startValue) ?: return ""
    val endWeekStart = parseEditorDate(endWeekStartValue) ?: start
    val end = endWeekStart.plus(6, DateTimeUnit.DAY)
    return "${formatPreviewDate(start.toString())} - ${formatPreviewDate(end.toString())}"
}

private fun parseEditorDate(value: String): LocalDate? {
    val normalized = value.trim().substringBefore('T')
    val isoParts = normalized.split('-')
    if (isoParts.size == 3) {
        return runCatching { LocalDate(isoParts[0].toInt(), isoParts[1].toInt(), isoParts[2].toInt()) }.getOrNull()
    }
    val displayParts = normalized.split(' ').filter(String::isNotBlank)
    if (displayParts.size != 3) return null
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val month = months.indexOfFirst { it.equals(displayParts[1], ignoreCase = true) } + 1
    if (month !in 1..12) return null
    return runCatching { LocalDate(displayParts[2].toInt(), month, displayParts[0].toInt()) }.getOrNull()
}

internal fun apiTime(value: String): String {
    val normalized = value.trim().uppercase()
    val meridiem = when {
        normalized.endsWith("AM") -> "AM"
        normalized.endsWith("PM") -> "PM"
        else -> ""
    }
    val clock = normalized.removeSuffix("AM").removeSuffix("PM").trim()
    val parts = clock.split(":")
    val hour = parts.getOrNull(0)?.toIntOrNull() ?: return ""
    val minute = parts.getOrNull(1)?.toIntOrNull() ?: return ""
    val convertedHour = when {
        meridiem == "AM" && hour == 12 -> 0
        meridiem == "PM" && hour < 12 -> hour + 12
        else -> hour
    }
    return convertedHour.coerceIn(0, 23).toString().padStart(2, '0') + ":" + minute.coerceIn(0, 59).toString().padStart(2, '0')
}

@Composable
internal fun PreviewTimeTableDialog(
    selectedClass: String,
    selectedSection: String,
    fromDate: String,
    duration: String,
    slots: List<EditorSlot>,
    values: List<String>,
    specialSchedules: List<SpecialDaySchedule>,
    specialDays: List<String>,
    weeklyOffDays: List<String>,
    weekStartDate: String = fromDate,
    status: String? = null,
    enableWeekNavigation: Boolean = false,
    title: String = "Preview Time Table",
    subtitle: String = "Review the timetable before saving or publishing.",
    onDismiss: () -> Unit
) {
    var weekPickerOpen by remember { mutableStateOf(false) }
    var selectedWeekStart by remember(weekStartDate, fromDate) {
        mutableStateOf(normalizedWeekStart(weekStartDate.ifBlank { fromDate }))
    }
    val weekEnd = selectedWeekStart?.plus(6, DateTimeUnit.DAY)
    val weekLabel = if (selectedWeekStart != null && weekEnd != null) {
        "${formatPreviewDate(selectedWeekStart.toString())} – ${formatPreviewDate(weekEnd.toString())}"
    } else "$fromDate • $duration"

    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1480.dp,
        maxDialogHeight = 900.dp
    ) {
        AppDialogHeader(
            title = title,
            subtitle = subtitle,
            onClose = onDismiss,
            colors = AppDialogDefaultColors,
            height = 92.dp
        )
        AppDialogDivider()
        AppDialogBody(horizontalPadding = 28.dp, verticalPadding = 20.dp) {
            Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(18.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    PreviewSummaryField("Class / Section", "$selectedClass-$selectedSection", Modifier.weight(1f))
                    if (enableWeekNavigation) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Spacer(Modifier.height(22.dp))
                            AppOutlinedButton(
                                onClick = { selectedWeekStart = selectedWeekStart?.minus(7, DateTimeUnit.DAY) },
                                modifier = Modifier.size(48.dp),
                                contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp)
                            ) {
                                Icon(painterResource(Res.drawable.chevron_left), "Previous week", Modifier.size(18.dp), tint = AppPrimary)
                            }
                        }
                        Column(Modifier.weight(1.6f), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            Text("Week", color = AppNavy, fontSize = 13.sp)
                            Box(Modifier.fillMaxWidth().clickable { weekPickerOpen = true }) {
                                AppDialogTextField(
                                    value = weekLabel,
                                    onValueChange = {},
                                    placeholder = "Select week",
                                    modifier = Modifier.fillMaxWidth(),
                                    readOnly = true,
                                    fieldHeight = 48.dp,
                                    trailingIcon = {
                                        Icon(painterResource(Res.drawable.calendar), "Choose week", Modifier.size(18.dp), tint = AppPrimary)
                                    }
                                )
                            }
                        }
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Spacer(Modifier.height(22.dp))
                            AppOutlinedButton(
                                onClick = { selectedWeekStart = selectedWeekStart?.plus(7, DateTimeUnit.DAY) },
                                modifier = Modifier.size(48.dp),
                                contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp)
                            ) {
                                Icon(painterResource(Res.drawable.chevron_right), "Next week", Modifier.size(18.dp), tint = AppPrimary)
                            }
                        }
                    } else {
                        PreviewSummaryField("Weeks", duration.ifBlank { "—" }, Modifier.weight(1.6f))
                    }
                    if (status != null) {
                        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            Spacer(Modifier.height(22.dp))
                            Box(
                                Modifier
                                    .height(48.dp)
                                    .border(1.dp, TimeTableStatusColor(status), RoundedCornerShape(8.dp))
                                    .padding(horizontal = 18.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(status, color = TimeTableStatusColor(status), fontSize = 14.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
                            }
                        }
                    } else Spacer(Modifier.weight(1f))
                }
                PreviewGrid(
                    title = "Weekly Schedule",
                    slots = slots,
                    values = values,
                    days = EditorAllDays.filter { day -> specialDays.none { it.take(3).equals(day, true) } },
                    allDays = EditorAllDays,
                    weeklyOffDays = weeklyOffDays,
                    weekStartDate = selectedWeekStart?.toString() ?: weekStartDate
                )
                val sharedSpecialSlots = specialSchedules.firstOrNull()?.slots.orEmpty()
                val sharedSpecialValues = specialSchedules.flatMap { schedule ->
                    sharedSpecialSlots.indices.map { schedule.values.getOrElse(it) { "" } }
                }
                if (specialSchedules.isNotEmpty()) {
                    PreviewGrid(
                        title = "Special Schedule • ${specialSchedules.joinToString(", ") { it.day }}",
                        slots = sharedSpecialSlots,
                        values = sharedSpecialValues,
                        days = specialSchedules.map { it.day.take(3) },
                        allDays = specialSchedules.map { it.day.take(3) },
                        weeklyOffDays = emptyList(),
                        weekStartDate = selectedWeekStart?.toString() ?: weekStartDate
                    )
                }
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Primary)),
            horizontalArrangement = Arrangement.End
        )
    }
    if (weekPickerOpen) {
        val pickerValue = selectedWeekStart?.toString()?.let(::formatPreviewDate).orEmpty()
        AppDialogDatePickerDialog(
            value = pickerValue,
            onDismiss = { weekPickerOpen = false },
            onDateSelected = { selectedWeekStart = normalizedWeekStart(it); weekPickerOpen = false },
            colors = AppDialogDefaultColors,
            weekSelection = true
        )
    }
}

private fun normalizedWeekStart(value: String): LocalDate? {
    val trimmed = value.trim().substringBefore('T')
    val date = runCatching {
        val iso = trimmed.split('-')
        if (iso.size == 3) LocalDate(iso[0].toInt(), iso[1].toInt(), iso[2].toInt())
        else {
            val parts = trimmed.split(' ')
            val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
            val month = months.indexOfFirst { it.equals(parts[1], true) } + 1
            LocalDate(parts[2].toInt(), month, parts[0].toInt())
        }
    }.getOrNull() ?: return null
    return date.minus(date.dayOfWeek.isoDayNumber - 1, DateTimeUnit.DAY)
}

@Composable
private fun PreviewSummaryField(label: String, value: String, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp)
        Box(
            Modifier
                .fillMaxWidth()
                .height(48.dp)
                .border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp))
                .padding(horizontal = 14.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            Text(value, color = AppNavy, fontSize = 14.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
internal fun PreviewGrid(
    title: String,
    slots: List<EditorSlot>,
    values: List<String>,
    days: List<String>,
    allDays: List<String>,
    weeklyOffDays: List<String>,
    weekStartDate: String
) {
    val scroll = rememberScrollState()
    val visibleColumnCount = maxOf(5, slots.size)
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(title, color = AppNavy, fontSize = 15.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val dayColumnWidth = 84.dp
            val slotColumnWidth = maxOf((maxWidth - dayColumnWidth) / 5, 190.dp)
            val contentWidth = dayColumnWidth + slotColumnWidth * visibleColumnCount.toFloat()
            Box(Modifier.fillMaxWidth().horizontalScroll(scroll).border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp))) {
                Column(Modifier.width(contentWidth)) {
                    Row(Modifier.background(EditorHeaderBg).height(48.dp), verticalAlignment = Alignment.CenterVertically) {
                        PreviewTableCell("Day", Modifier.width(dayColumnWidth), bold = true)
                        slots.forEach { slot -> PreviewTableCell(slot.label, Modifier.width(slotColumnWidth), bold = true) }
                        repeat(visibleColumnCount - slots.size) {
                            PreviewTableCell("", Modifier.width(slotColumnWidth), bold = true)
                        }
                    }
                    days.forEach { day ->
                        val isOff = weeklyOffDays.any { day.equals(it.take(3), true) }
                        Row(Modifier.height(64.dp).background(if (isOff) EditorHolidayBg else Color.White), verticalAlignment = Alignment.CenterVertically) {
                            PreviewTableDayCell(day, formatTimetableDayDate(weekStartDate, day), Modifier.width(dayColumnWidth).height(64.dp), AppPrimary)
                            if (isOff) {
                                Box(
                                    Modifier
                                        .width(slotColumnWidth * visibleColumnCount.toFloat())
                                        .height(64.dp)
                                        .border(1.dp, AppDialogDefaultColors.border),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text("School Holiday", color = AppNavy, fontSize = 14.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
                                }
                            } else {
                                slots.indices.forEach { slotIndex ->
                                    val valueIndex = allDays.indexOfFirst { it.equals(day, true) } * slots.size + slotIndex
                                    PreviewTableCell(values.getOrElse(valueIndex) { "—" }, Modifier.width(slotColumnWidth), height = 64.dp)
                                }
                                repeat(visibleColumnCount - slots.size) {
                                    PreviewTableCell("", Modifier.width(slotColumnWidth), height = 64.dp)
                                }
                            }
                        }
                    }
                }
            }
            AdminListHorizontalScrollbar(state = scroll, modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun PreviewTableCell(value: String, modifier: Modifier, bold: Boolean = false, color: Color = AppNavy, height: androidx.compose.ui.unit.Dp = 48.dp) {
    Box(
        modifier
            .height(height)
            .border(1.dp, AppDialogDefaultColors.border)
            .padding(horizontal = 10.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            value,
            color = color,
            fontSize = 13.sp,
            fontWeight = if (bold) androidx.compose.ui.text.font.FontWeight.SemiBold else androidx.compose.ui.text.font.FontWeight.Normal,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun PreviewTableDayCell(day: String, date: String, modifier: Modifier, color: Color) {
    Column(
        modifier.border(1.dp, AppDialogDefaultColors.border).padding(horizontal = 8.dp, vertical = 4.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text(day, color = color, fontSize = 13.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
        if (date.isNotBlank()) Text(date, color = AppNavy, fontSize = 11.sp)
    }
}

@Composable
private fun TimeRangePickerDialog(initialRange: String = "", onDismiss: () -> Unit, onAdd: (String) -> Unit) {
    val initialParts = initialRange.split(" – ", limit = 2)
    val initialTimes = remember(initialRange) {
        val start = parseSlotTime(initialParts.getOrNull(0).orEmpty()) ?: (8 * 60 + 30)
        val end = parseSlotTime(initialParts.getOrNull(1).orEmpty()) ?: (start + 40).coerceAtMost(23 * 60 + 59)
        (start to end)
    }
    var startMinutes by remember(initialTimes) { mutableStateOf(initialTimes.first) }
    var endMinutes by remember(initialTimes) { mutableStateOf(initialTimes.second) }
    var durationText by remember(initialTimes) {
        mutableStateOf((initialTimes.second - initialTimes.first).takeIf { it > 0 }?.toString() ?: "40")
    }
    var error by remember { mutableStateOf("") }
    val startTime = formatSlotTime(startMinutes)
    val endTime = formatSlotTime(endMinutes)

    fun updateStartTime(minutes: Int) {
        startMinutes = minutes
        val duration = durationText.toIntOrNull()
        if (duration != null && duration > 0 && minutes + duration < 24 * 60) {
            endMinutes = minutes + duration
            error = ""
        } else {
            error = "The time slot must end before midnight."
        }
    }

    fun updateDuration(value: String) {
        durationText = value
        val duration = value.toIntOrNull()
        when {
            duration == null -> error = "Enter the duration in whole minutes."
            duration <= 0 -> error = "Duration must be at least 1 minute."
            startMinutes + duration >= 24 * 60 -> error = "The time slot must end before midnight."
            else -> {
                endMinutes = startMinutes + duration
                error = ""
            }
        }
    }

    fun updateEndTime(minutes: Int) {
        endMinutes = minutes
        val duration = minutes - startMinutes
        if (duration > 0) {
            durationText = duration.toString()
            error = ""
        } else {
            error = "End time must be after start time."
        }
    }

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Compact, maxDialogHeight = 700.dp) {
        AppDialogHeader(
            title = "Add Time Slot",
            subtitle = "Choose the time range for the new timetable column.",
            onClose = onDismiss,
            icon = Res.drawable.clock,
            colors = AppDialogDefaultColors
        )
        AppDialogDivider()
        AppDialogBody(horizontalPadding = 20.dp, verticalPadding = 20.dp) { _ ->
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                TimeOfDaySelector("Start Time", startMinutes, ::updateStartTime)
                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    Text("Duration", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        AppDialogTextField(
                            value = durationText,
                            onValueChange = ::updateDuration,
                            placeholder = "Minutes",
                            modifier = Modifier.width(180.dp),
                            keyboardType = KeyboardType.Number,
                            maxLength = 4
                        )
                        Text("minutes", color = AppMuted, fontSize = 14.sp)
                    }
                }
                TimeOfDaySelector("End Time", endMinutes, ::updateEndTime)
                Text(
                    "Changing duration updates End Time. Changing End Time recalculates Duration.",
                    color = AppMuted,
                    fontSize = 12.sp
                )
                if (error.isNotBlank()) Text(error, color = AppDialogDefaultColors.danger, fontSize = 13.sp)
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", onDismiss),
                AppDialogFooterAction("Add Slot", {
                    val duration = durationText.toIntOrNull()
                    if (duration == null || duration <= 0 || endMinutes <= startMinutes || endMinutes - startMinutes != duration) {
                        error = "Enter a valid start time, end time, and duration."
                    } else onAdd("$startTime – $endTime")
                }, AppDialogActionStyle.Primary, enabled = error.isBlank())
            ),
            colors = AppDialogDefaultColors,
            equalWeightButtons = true
        )
    }
}

@Composable
private fun TimeOfDaySelector(label: String, value: Int, onValueChange: (Int) -> Unit) {
    val hour24 = value / 60
    val minute = value % 60
    val hour12 = (hour24 % 12).let { if (it == 0) 12 else it }
    val meridiem = if (hour24 < 12) "AM" else "PM"
    val hourOptions = (1..12).map { it.toString().padStart(2, '0') }
    val minuteOptions = (0..59).map { it.toString().padStart(2, '0') }
    var hourExpanded by remember { mutableStateOf(false) }
    var minuteExpanded by remember { mutableStateOf(false) }

    fun emit(hour: Int = hour12, minuteValue: Int = minute, period: String = meridiem) {
        val hourOfDay = (hour % 12) + if (period == "PM") 12 else 0
        onValueChange(hourOfDay * 60 + minuteValue)
    }

    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            AppDialogSelectField(
                value = hour12.toString().padStart(2, '0'), placeholder = "HH", expanded = hourExpanded,
                options = hourOptions, onToggleExpanded = { hourExpanded = true }, onDismissExpanded = { hourExpanded = false },
                onSelected = { emit(hour = it.toInt()) }, modifier = Modifier.width(76.dp), fieldHeight = 40.dp
            )
            Text(":", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
            AppDialogSelectField(
                value = minute.toString().padStart(2, '0'), placeholder = "MM", expanded = minuteExpanded,
                options = minuteOptions, onToggleExpanded = { minuteExpanded = true }, onDismissExpanded = { minuteExpanded = false },
                onSelected = { emit(minuteValue = it.toInt()) }, modifier = Modifier.width(76.dp), fieldHeight = 40.dp
            )
            Row(
                modifier = Modifier
                    .border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(6.dp))
                    .height(40.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                listOf("AM", "PM").forEach { period ->
                    val selected = period == meridiem
                    Box(
                        modifier = Modifier
                            .width(52.dp)
                            .height(40.dp)
                            .background(if (selected) AppPrimary.copy(alpha = 0.10f) else Color.Transparent)
                            .clickable { emit(period = period) },
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            period,
                            color = if (selected) AppPrimary else AppNavy,
                            fontSize = 13.sp,
                            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal
                        )
                    }
                }
            }
        }
    }
}

private fun parseSlotTime(value: String): Int? {
    val parts = value.trim().uppercase().split(Regex("\\s+"))
    val clock = parts.firstOrNull()?.split(":") ?: return null
    if (clock.size != 2) return null
    val minute = clock[1].toIntOrNull()?.takeIf { it in 0..59 } ?: return null
    val hour24 = when {
        parts.size == 1 -> clock[0].toIntOrNull()?.takeIf { it in 0..23 } ?: return null
        parts.size == 2 && parts[1] in setOf("AM", "PM") -> {
            val hour12 = clock[0].toIntOrNull()?.takeIf { it in 1..12 } ?: return null
            (hour12 % 12) + if (parts[1] == "PM") 12 else 0
        }
        else -> return null
    }
    return hour24 * 60 + minute
}

private fun formatSlotTime(minutesFromMidnight: Int): String {
    val hour24 = minutesFromMidnight / 60
    val hour12 = (hour24 % 12).let { if (it == 0) 12 else it }
    val minute = minutesFromMidnight % 60
    val meridiem = if (hour24 < 12) "AM" else "PM"
    return "${hour12.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} $meridiem"
}

private fun nextSlotRange(slots: List<EditorSlot>): String? {
    if (slots.isEmpty()) return "08:30 AM – 09:10 AM"
    val previous = slots.last().label.split(" – ", limit = 2)
    val previousStart = parseSlotTime(previous.getOrNull(0).orEmpty()) ?: return "08:30 AM – 09:10 AM"
    val previousEnd = parseSlotTime(previous.getOrNull(1).orEmpty()) ?: return "08:30 AM – 09:10 AM"
    val previousDuration = previousEnd - previousStart
    if (previousDuration <= 0) return "08:30 AM – 09:10 AM"

    val latestEnd = 23 * 60 + 59
    val remainingMinutes = latestEnd - previousEnd
    if (remainingMinutes <= 0) return null
    val nextDuration = minOf(previousDuration, remainingMinutes)
    return "${formatSlotTime(previousEnd)} – ${formatSlotTime(previousEnd + nextDuration)}"
}

@Composable
private fun EditorGrid(title: String, slots: List<EditorSlot>, values: List<String>, days: List<String>, allDays: List<String>, weeklyOffDays: List<String>, readOnly: Boolean, subjects: List<String>, onAddSlot: (() -> Unit)? = null, onEditSlot: (Int, EditorSlot) -> Unit, onSlotChanged: (Int, EditorSlot) -> Unit, onSlotRemoved: (Int) -> Unit, onValueChanged: (Int, String) -> Unit) {
    val scroll = rememberScrollState()
    val visibleColumnCount = maxOf(5, slots.size)
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Text(title, color = AppNavy, fontSize = 15.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
            if (onAddSlot != null && !readOnly) EditorOutlineButton("Add Time Slot", Modifier.width(196.dp), onAddSlot)
        }
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val dayColumnWidth = 84.dp
            // Keep slot columns at the five-slot layout width as more periods are
            // added. Extra columns extend the grid horizontally and use its scrollbar.
            val slotColumnWidth = maxOf((maxWidth - dayColumnWidth) / 5, 190.dp)
            val contentWidth = dayColumnWidth + slotColumnWidth * visibleColumnCount.toFloat()
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Box(Modifier.fillMaxWidth().horizontalScroll(scroll).border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(6.dp))) {
                    Column(Modifier.width(contentWidth)) {
                Row(Modifier.background(EditorHeaderBg).height(48.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.width(dayColumnWidth).height(48.dp).border(1.dp, AppDialogDefaultColors.border), contentAlignment = Alignment.Center) {
                        Text("Day", color = AppNavy, fontSize = 12.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
                    }
                    slots.forEachIndexed { slotIndex, slot ->
                        Box(Modifier.width(slotColumnWidth).height(48.dp).border(1.dp, AppDialogDefaultColors.border).padding(horizontal = 5.dp), contentAlignment = Alignment.Center) {
                            com.schoolconnect.app.designsystem.AppDialogTextField(
                                value = slot.label,
                                onValueChange = { onSlotChanged(slotIndex, EditorSlot(it)) },
                                placeholder = "HH:MM – HH:MM",
                                modifier = Modifier.fillMaxWidth().height(36.dp),
                                readOnly = readOnly,
                                fontSize = 10.sp,
                                leadingIconStartPadding = 4.dp,
                                leadingIcon = if (!readOnly) ({
                                    Icon(
                                        painterResource(Res.drawable.clock),
                                        contentDescription = "Pick time for slot ${slotIndex + 1}",
                                        tint = AppPrimary,
                                        modifier = Modifier.size(21.dp).clickable { onEditSlot(slotIndex, slot) }
                                    )
                                }) else null,
                                trailingIcon = if (!readOnly) ({
                                    Box(
                                        modifier = Modifier
                                            .size(24.dp)
                                            .clickable { onSlotRemoved(slotIndex) },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(
                                            painterResource(Res.drawable.trash),
                                            contentDescription = "Delete time slot ${slotIndex + 1}",
                                            tint = Color(0xFFB42318),
                                            modifier = Modifier.size(15.dp)
                                        )
                                    }
                                }) else null
                            )
                        }
                    }
                    repeat(visibleColumnCount - slots.size) {
                        Box(Modifier.width(slotColumnWidth).height(48.dp).border(1.dp, AppDialogDefaultColors.border))
                    }
                }
                days.forEach { day ->
                    val isOff = weeklyOffDays.any { day.equals(it.take(3), true) }
                    Row(Modifier.height(48.dp).background(if (isOff) EditorHolidayBg else Color.White), verticalAlignment = Alignment.CenterVertically) {
                        Box(Modifier.width(dayColumnWidth).height(48.dp).border(1.dp, AppDialogDefaultColors.border), contentAlignment = Alignment.Center) {
                        Text(day, color = AppNavy, fontSize = 14.sp)
                        }
                        if (isOff) {
                            Box(Modifier.width((slotColumnWidth * visibleColumnCount.toFloat())).height(48.dp).border(1.dp, AppDialogDefaultColors.border), contentAlignment = Alignment.Center) {
                                Text("School Holiday", color = AppNavy, fontSize = 12.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold)
                            }
                        } else {
                            slots.indices.forEach { slotIndex ->
                                val valueIndex = allDays.indexOfFirst { it.equals(day, true) } * slots.size + slotIndex
                                EditorCell(values.getOrElse(valueIndex) { "" }, readOnly, subjects, Modifier.width(slotColumnWidth).height(48.dp)) { onValueChanged(valueIndex, it) }
                            }
                            repeat(visibleColumnCount - slots.size) {
                                Box(Modifier.width(slotColumnWidth).height(48.dp).border(1.dp, AppDialogDefaultColors.border))
                            }
                        }
                    }
                }
                    }
                }
                AdminListHorizontalScrollbar(state = scroll, modifier = Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
private fun EditorCell(value: String, readOnly: Boolean, subjects: List<String>, modifier: Modifier, onSelected: (String) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier.border(1.dp, AppDialogDefaultColors.border).padding(horizontal = 5.dp, vertical = 4.dp)) {
        AppDialogSelectField(value, "Select Subject", expanded, subjects, { if (!readOnly && subjects.isNotEmpty()) expanded = true }, { expanded = false }, { expanded = false; onSelected(it) }, Modifier.fillMaxWidth(), fieldHeight = 40.dp, fontSize = 14.sp)
    }
}

@Composable
private fun EditorSelect(label: String, value: String, options: List<String>, modifier: Modifier, fieldHeight: androidx.compose.ui.unit.Dp = com.schoolconnect.app.designsystem.AppDialogFieldHeight, onSelected: (String) -> Unit) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 15.sp)
        var expanded by remember { mutableStateOf(false) }
        AppDialogSelectField(value, "Select", expanded, options, { expanded = true }, { expanded = false }, { expanded = false; onSelected(it) }, fieldHeight = fieldHeight)
    }
}

private fun TimetableClassSectionOption.displayLabel(): String =
    listOf(className.trim(), sectionName.trim()).filter(String::isNotBlank).joinToString("-")

@Composable
private fun EditorMultiSelect(
    label: String,
    selected: List<String>,
    options: List<String>,
    modifier: Modifier,
    fieldHeight: androidx.compose.ui.unit.Dp = com.schoolconnect.app.designsystem.AppDialogFieldHeight,
    onChanged: (List<String>) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 15.sp)
        AppDialogMultiSelectField(
            selectedValues = selected,
            placeholder = "Select days",
            options = options,
            expanded = expanded,
            onToggleExpanded = { expanded = !expanded },
            onDismissExpanded = { expanded = false },
            onSelectionChanged = onChanged,
            modifier = Modifier.fillMaxWidth(),
            searchPlaceholder = "Search days",
            emptyResultsText = "No days found",
            fieldHeight = fieldHeight
        )
    }
}

@Composable
private fun EditorDate(label: String, value: String, modifier: Modifier, readOnly: Boolean = false, weekSelection: Boolean = false, onChanged: (String) -> Unit) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp)
        AppDialogDateField(value, onChanged, Modifier.fillMaxWidth(), readOnly = readOnly, weekSelection = weekSelection)
    }
}

@Composable
private fun EditorWeekRangeField(label: String, value: String, modifier: Modifier, helperText: String = "", onClick: () -> Unit) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(44.dp)
                .border(1.dp, AppDialogDefaultColors.inputBorder, RoundedCornerShape(8.dp))
                .clickable(onClick = onClick)
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Icon(painterResource(Res.drawable.calendar), "Select timetable weeks", Modifier.size(18.dp), tint = AppPrimary)
            Text(
                text = value.ifBlank { "Select week(s)" },
                color = if (value.isBlank()) AppMuted else AppNavy,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )
            Icon(painterResource(Res.drawable.chevron_down), "Open week selector", Modifier.size(16.dp), tint = AppNavy)
        }
        if (helperText.isNotBlank()) {
            Text(helperText, color = AppMuted, fontSize = 11.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
private fun EditorOutlineButton(label: String, modifier: Modifier, onClick: () -> Unit) {
    AppOutlinedButton(
        onClick = onClick,
        modifier = modifier.height(44.dp),
        border = BorderStroke(1.dp, AppDialogDefaultColors.inputBorder)
    ) {
        Text(label, fontSize = 14.sp, fontWeight = androidx.compose.ui.text.font.FontWeight.Medium, maxLines = 1, softWrap = false)
    }
}
